import {
  hqImpersonationEndResponseSchema,
  notificationMutationResponseSchema,
  schoolNotificationsResponseSchema,
  schoolCatalogResponseSchema,
  schoolCatalogOptionsResponseSchema,
  schoolLessonReportResponseSchema,
  schoolLimitsResponseSchema,
  schoolReadinessResponseSchema,
  schoolTeacherEscalationMutationResponseSchema,
  schoolTeacherEscalationsResponseSchema,
  teacherDutyResponseSchema,
  teacherDutyRuleMutationResponseSchema,
  type DataExportFormat,
  type SchoolGradeCreateRequest,
  type SchoolGradeUpdateRequest,
  type SchoolReadinessUpdateRequest,
  type SchoolTeacherEscalationUpdateRequest,
  type TeacherDutyRuleInput,
  type TeacherDutySettingsUpdateRequest,
} from "@kindtutor/api-contract";
import {
  fetchApiBlob,
  fetchApiJson,
  localizedApiErrorSchema as apiErrorSchema,
  throwApiError,
} from "@kindtutor/auth-client";

import { apiBaseURL, apiFetch } from "./auth-client";

export async function endHqSupportAccess() {
  return fetchApiJson(
    apiFetch,
    `${apiBaseURL}/v1/hq/impersonations/active`,
    {
      method: "DELETE",
      credentials: "include",
      headers: { Accept: "application/json" },
    },
    hqImpersonationEndResponseSchema,
    "Nie udało się zakończyć dostępu wsparcia.",
  );
}

export async function downloadSchoolLessonsExport(
  schoolId: string,
  input: {
    format: DataExportFormat;
    days: 7 | 30 | 90;
    gradeId?: string;
    schoolSubjectId?: string;
  },
) {
  const query = new URLSearchParams({
    format: input.format,
    days: String(input.days),
  });
  if (input.gradeId) query.set("gradeId", input.gradeId);
  if (input.schoolSubjectId) {
    query.set("schoolSubjectId", input.schoolSubjectId);
  }
  return fetchApiBlob(
    apiFetch,
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/exports/lessons?${query}`,
    { credentials: "include", headers: { Accept: "*/*" } },
    "Nie udało się przygotować eksportu.",
    `kindtutor-lessons.${input.format}`,
  );
}

export async function fetchSchoolLessonReport(
  schoolId: string,
  input: {
    days: 7 | 30 | 90;
    gradeId?: string;
    schoolSubjectId?: string;
  },
) {
  const query = new URLSearchParams({ days: String(input.days) });
  if (input.gradeId) query.set("gradeId", input.gradeId);
  if (input.schoolSubjectId) {
    query.set("schoolSubjectId", input.schoolSubjectId);
  }
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/reports/lessons?${query}`,
    { credentials: "include", headers: { Accept: "application/json" } },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się wczytać raportu.",
    );
  }
  return schoolLessonReportResponseSchema.parse(body);
}

export async function fetchSchoolCatalog(schoolId: string) {
  return fetchApiJson(
    apiFetch,
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/catalog`,
    {
      credentials: "include",
      headers: { Accept: "application/json" },
    },
    schoolCatalogResponseSchema,
    "Nie udało się wczytać klas i przedmiotów.",
  );
}

export async function fetchSchoolReadiness(schoolId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/readiness`,
    { credentials: "include", headers: { Accept: "application/json" } },
  );
  if (!response.ok)
    await throwApiError(
      response,
      "Nie udało się wczytać przygotowania szkoły.",
    );
  return schoolReadinessResponseSchema.parse(await response.json());
}

export async function updateSchoolReadiness(
  schoolId: string,
  input: SchoolReadinessUpdateRequest,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/readiness`,
    {
      method: "PUT",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success ? error.data.error.message : "Nie udało się zapisać kroku.",
    );
  }
  return schoolReadinessResponseSchema.parse(body);
}

export async function fetchSchoolCatalogOptions(schoolId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/catalog/options`,
    { credentials: "include", headers: { Accept: "application/json" } },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać opcji katalogu.");
  return schoolCatalogOptionsResponseSchema.parse(await response.json());
}

async function catalogMutation(
  url: string,
  method: "POST" | "PUT",
  input: SchoolGradeCreateRequest | SchoolGradeUpdateRequest,
) {
  const response = await apiFetch(url, {
    method,
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success ? error.data.error.message : "Nie udało się zapisać klasy.",
    );
  }
  return schoolCatalogResponseSchema.parse(body);
}

export function createSchoolGrade(
  schoolId: string,
  input: SchoolGradeCreateRequest,
) {
  return catalogMutation(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/grades`,
    "POST",
    input,
  );
}

export function updateSchoolGrade(
  schoolId: string,
  gradeId: string,
  input: SchoolGradeUpdateRequest,
) {
  return catalogMutation(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/grades/${encodeURIComponent(gradeId)}`,
    "PUT",
    input,
  );
}

export {
  approveSchoolMaterial,
  deleteSchoolMaterial,
  fetchAllSchoolMaterials,
  fetchSchoolMaterials,
  getSchoolMaterialDownloadUrl,
  reindexSchoolMaterial,
  uploadSchoolMaterial,
} from "./api/materials";

export async function fetchSchoolLimits(schoolId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/limits`,
    {
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać limitów szkoły.");
  return schoolLimitsResponseSchema.parse(await response.json());
}

export {
  addSchoolIncidentComment,
  createSchoolIncident,
  deleteSchoolIncidentAttachment,
  downloadSchoolIncidentAttachment,
  fetchSchoolIncident,
  fetchSchoolIncidents,
  uploadSchoolIncidentAttachment,
} from "./api/incidents";

export async function fetchTeacherEscalations(schoolId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/teacher-escalations`,
    { credentials: "include", headers: { Accept: "application/json" } },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać próśb uczniów.");
  return schoolTeacherEscalationsResponseSchema.parse(await response.json());
}

export async function fetchTeacherDuty(schoolId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/teacher-duty`,
    { credentials: "include", headers: { Accept: "application/json" } },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać dyżurów nauczycieli.");
  return teacherDutyResponseSchema.parse(await response.json());
}

export async function updateTeacherDutySettings(
  schoolId: string,
  input: TeacherDutySettingsUpdateRequest,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/teacher-duty`,
    {
      method: "PUT",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się zapisać ustawień dyżuru.");
  return teacherDutyResponseSchema.parse(await response.json());
}

export async function createTeacherDutyRule(
  schoolId: string,
  input: TeacherDutyRuleInput,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/teacher-duty/rules`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się dodać reguły dyżuru.");
  return teacherDutyRuleMutationResponseSchema.parse(await response.json());
}

export async function deleteTeacherDutyRule(schoolId: string, ruleId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/teacher-duty/rules/${encodeURIComponent(ruleId)}`,
    { method: "DELETE", credentials: "include" },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się usunąć reguły dyżuru.");
}

export async function fetchSchoolNotifications(schoolId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/notifications`,
    { credentials: "include", headers: { Accept: "application/json" } },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać powiadomień.");
  return schoolNotificationsResponseSchema.parse(await response.json());
}

export async function markSchoolNotificationRead(
  schoolId: string,
  notificationId: string,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/notifications/${encodeURIComponent(notificationId)}/read`,
    {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się oznaczyć powiadomienia.");
  return notificationMutationResponseSchema.parse(await response.json());
}

export async function markAllSchoolNotificationsRead(schoolId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/notifications/read-all`,
    {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się oznaczyć powiadomień.");
  return notificationMutationResponseSchema.parse(await response.json());
}

export async function updateTeacherEscalation(
  schoolId: string,
  escalationId: string,
  input: SchoolTeacherEscalationUpdateRequest,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/teacher-escalations/${encodeURIComponent(escalationId)}`,
    {
      method: "PUT",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się zaktualizować prośby.",
    );
  }
  return schoolTeacherEscalationMutationResponseSchema.parse(body);
}

export {
  acceptSchoolInvitation,
  cancelSchoolInvitation,
  createSchoolDeviceCommand,
  createSchoolDeviceEnrollment,
  createSchoolInvitation,
  fetchSchoolDevices,
  fetchSchoolInvitation,
  fetchSchoolLearningSettings,
  fetchSchoolMembers,
  fetchSchoolProfile,
  resetSchoolDevicePairing,
  revokeSchoolMember,
  updateSchoolGuardianCode,
  updateSchoolLearningSettings,
  updateSchoolMemberRole,
  updateSchoolProfile,
} from "./api/account";
